import { LitElement, html, nothing, type TemplateResult, type PropertyValues } from 'lit';
import styles from './styles.js';
import { property, query, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { classMap } from 'lit/directives/class-map.js';
import { styleMap } from 'lit/directives/style-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import type { Placement, MenuItem, MenuItemCommandEvent, MenuItemSelectEventDetail } from '../types.js';
import { emit } from '../utils/dom.utils.js';
import { PlacementController } from '../utils/placement.controller.js';

/** Internal flat render entry — built from `items` at render time. */
interface RenderEntry {
  item: MenuItem;
  isGroupHeader: boolean;
  renderIdx: number;
}

/**
 * uwc-menu
 *
 * Menu component with two rendering modes:
 *
 * - **Popup** (default): a floating overlay anchored to a trigger element.
 *   Use the `trigger` slot or `trigger-id` attribute to specify the activator.
 * - **Inline** (`inline` attribute): a static, always-visible panel — ideal for
 *   sidebars, toolbars, or demo pages.
 *
 * Supports separators, icons, badges, disabled states, submenus (hover/arrow),
 * grouped sections (`group: true`), `start` / `end` panel slots, selected-item
 * highlighting, and full keyboard navigation.
 *
 * @attr items          - MenuItem[] — the menu item model.
 * @attr inline         - Render as an always-visible static panel (no popup).
 * @attr trigger-id     - External trigger element id (popup mode).
 * @attr placement      - Default: bottom-start (popup mode).
 * @attr offset         - Gap px. Default: 6 (popup mode).
 *
 * @fires uwc-show
 * @fires uwc-hide
 * @fires uwc-item-select  — detail: { item, originalEvent }
 *
 * @slot trigger        - Activating element (popup mode only).
 * @slot start          - Custom content above the item list inside the panel.
 * @slot end            - Custom content below the item list inside the panel.
 *
 * @csspart panel
 * @csspart group-header
 * @csspart item
 * @csspart item-icon
 * @csspart item-label
 * @csspart item-badge
 * @csspart separator
 * @csspart submenu-panel
 *
 * @cssprop --uwc-menu-bg
 * @cssprop --uwc-menu-border
 * @cssprop --uwc-menu-radius
 * @cssprop --uwc-menu-shadow
 * @cssprop --uwc-menu-item-hover-bg
 * @cssprop --uwc-menu-item-hover-color
 * @cssprop --uwc-menu-item-disabled-color
 * @cssprop --uwc-menu-item-font-size
 * @cssprop --uwc-menu-item-selected-bg
 * @cssprop --uwc-menu-item-selected-color
 * @cssprop --uwc-menu-item-danger-color
 * @cssprop --uwc-menu-group-header-color
 * @cssprop --uwc-menu-z
 * @cssprop --uwc-menu-duration
 */
export class UwcMenu extends LitElement {

  @property({ type: String,  attribute: 'trigger-id'  }) triggerId?: string;
  @property({ type: String,  attribute: 'selected-id', reflect: true }) selectedId?: string;
  @property({ type: Array                              }) items: MenuItem[] = [];
  @property({ type: String                             }) placement: Placement = 'bottom-start';
  @property({ type: Number                             }) offset = 6;
  @property({ type: Boolean, reflect: true             }) inline = false;

  @query('#panel') private _panel!: HTMLElement;

  @state() private _openSubmenuRIdx: number | null = null;
  @state() private _focusedRIdx    = -1;
  @state() private _selectedRIdx: number | null = null;

  private readonly _onMenuKeydown = this._handleMenuKeydown.bind(this);

  private _pc = new PlacementController(this, {
    getPanelEl: () => this._panel,

    onTriggerFound: (el) => {
      const onClick   = (e: Event): void => { e.stopPropagation(); this._pc.toggle(); };
      const onKeydown = (e: KeyboardEvent): void => {
        if ((e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') && !this._pc.open) {
          e.preventDefault();
          this._pc.show();
        }
      };
      el.setAttribute('aria-haspopup', 'menu');
      el.addEventListener('click',   onClick);
      el.addEventListener('keydown', onKeydown as EventListener);
      return () => {
        el.removeEventListener('click',   onClick);
        el.removeEventListener('keydown', onKeydown as EventListener);
        el.removeAttribute('aria-haspopup');
      };
    },

    dismissOnOutsideClick: true,
    dismissOnEscape:       true,

    afterShow: () => {
      this._focusedRIdx     = -1;
      this._openSubmenuRIdx = null;
      document.addEventListener('keydown', this._onMenuKeydown);
      emit(this, 'uwc-show');
      requestAnimationFrame(() => this._focusItem(this._firstFocusableRIdx()));
    },

    afterHide: () => {
      document.removeEventListener('keydown', this._onMenuKeydown);
      this._openSubmenuRIdx = null;
      this._focusedRIdx     = -1;
      emit(this, 'uwc-hide');
    },
  });

  // ── Lifecycle ────────────────────────────────────────────────────

  override firstUpdated(): void {
    if (this.inline) return;
    this._pc.firstUpdated(
      this.triggerId ?? null,
      this.shadowRoot!.querySelector<HTMLSlotElement>('slot[name="trigger"]'),
    );
  }

  protected override updated(changed: PropertyValues): void {
    super.updated(changed);
    if (changed.has('selectedId') || changed.has('items')) {
      this._syncSelectedFromId();
    }
    if (this.inline) return;
    this._pc.updated(
      changed as Map<PropertyKey, unknown>,
      this.triggerId ?? null,
      this.shadowRoot!.querySelector<HTMLSlotElement>('slot[name="trigger"]'),
    );
  }

  // ── Flat render list ──────────────────────────────────────────────

  private get _flatList(): RenderEntry[] {
    const out: RenderEntry[] = [];
    let i = 0;
    for (const item of this.items) {
      if (item.visible === false) continue;
      if (item.group === true) {
        out.push({ item, isGroupHeader: true, renderIdx: i++ });
        for (const child of item.items ?? []) {
          if (child.visible !== false) out.push({ item: child, isGroupHeader: false, renderIdx: i++ });
        }
      } else {
        out.push({ item, isGroupHeader: false, renderIdx: i++ });
      }
    }
    return out;
  }

  // ── Selected-id sync ─────────────────────────────────────────────

  private _syncSelectedFromId(): void {
    if (!this.selectedId) {
      this._selectedRIdx = null;
      return;
    }
    const entry = this._flatList.find(
      e => !e.isGroupHeader && !e.item.separator && e.item.id === this.selectedId,
    );
    this._selectedRIdx = entry ? entry.renderIdx : null;
  }

  // ── Item helpers ─────────────────────────────────────────────────

  private _navigable(): RenderEntry[] {
    return this._flatList.filter(
      e => !e.isGroupHeader && !e.item.separator && e.item.visible !== false && !e.item.disabled,
    );
  }

  private _firstFocusableRIdx(): number {
    return this._navigable()[0]?.renderIdx ?? -1;
  }

  private _focusItem(renderIdx: number): void {
    if (renderIdx < 0) return;
    this._focusedRIdx = renderIdx;
    const btn = this._panel?.querySelector<HTMLElement>(`[data-render-index="${renderIdx}"]`);
    btn?.focus();
  }

  // ── Keyboard navigation ──────────────────────────────────────────

  private _handleMenuKeydown(e: KeyboardEvent): void {
    const nav    = this._navigable();
    const curPos = nav.findIndex(entry => entry.renderIdx === this._focusedRIdx);

    switch (e.key) {
      case 'ArrowDown': {
        e.preventDefault();
        const next = nav[(curPos + 1) % nav.length];
        if (next) this._focusItem(next.renderIdx);
        break;
      }
      case 'ArrowUp': {
        e.preventDefault();
        const prev = nav[(curPos - 1 + nav.length) % nav.length];
        if (prev) this._focusItem(prev.renderIdx);
        break;
      }
      case 'Home':
        e.preventDefault();
        if (nav[0]) this._focusItem(nav[0].renderIdx);
        break;
      case 'End':
        e.preventDefault();
        if (nav.length) this._focusItem(nav[nav.length - 1].renderIdx);
        break;
      case 'ArrowRight': {
        const focused = this._flatList.find(entry => entry.renderIdx === this._focusedRIdx);
        if (focused && !focused.isGroupHeader && focused.item.items?.length && !focused.item.group) {
          this._openSubmenuRIdx = focused.renderIdx;
          requestAnimationFrame(() => {
            const subItem = this._panel?.querySelector<HTMLElement>(
              `[data-submenu-ridx="${focused.renderIdx}"] [data-sub-item-index="0"]`,
            );
            subItem?.focus();
          });
        }
        break;
      }
      case 'ArrowLeft':
        this._openSubmenuRIdx = null;
        break;
      case 'Tab':
        if (!this.inline) this._pc.hide();
        break;
    }
  }

  // ── Item command execution ────────────────────────────────────────

  private _executeItem(item: MenuItem, e: Event): void {
    if (item.disabled) return;
    const ev: MenuItemCommandEvent = { originalEvent: e, item };
    item.command?.(ev);
    emit<MenuItemSelectEventDetail>(this, 'uwc-item-select', { item, originalEvent: e });
    if (!this.inline && (!item.items?.length || item.group === true)) this._pc.hide();
  }

  private _handleItemClick(item: MenuItem, renderIdx: number, e: Event): void {
    e.stopPropagation();
    if (item.separator || item.disabled) return;
    if (item.items?.length && !item.group) {
      this._openSubmenuRIdx = this._openSubmenuRIdx === renderIdx ? null : renderIdx;
      return;
    }
    this._selectedRIdx = renderIdx;
    if (item.id) this.selectedId = item.id;
    this._executeItem(item, e);
  }

  private _handleItemMouseEnter(renderIdx: number, item: MenuItem): void {
    if (item.items?.length && !item.group) {
      this._openSubmenuRIdx = renderIdx;
    } else if (this._openSubmenuRIdx !== null) {
      this._openSubmenuRIdx = null;
    }
  }

  // ── Render helpers ────────────────────────────────────────────────

  private _renderEntry(entry: RenderEntry): TemplateResult {
    if (entry.isGroupHeader) {
      return html`
        <li class="group-header" part="group-header" role="presentation" aria-hidden="true">
          ${entry.item.label ?? ''}
        </li>`;
    }
    return this._renderItem(entry.item, entry.renderIdx);
  }

  private _renderItem(item: MenuItem, renderIdx: number): TemplateResult {
    if (item.separator) {
      return html`<li part="separator" class="separator" role="separator" aria-hidden="true"></li>`;
    }

    const hasSubmenu    = Boolean(item.items?.length && !item.group);
    const isSubmenuOpen = this._openSubmenuRIdx === renderIdx;
    const isFocused     = this._focusedRIdx    === renderIdx;
    const isSelected    = this._selectedRIdx   === renderIdx;

    return html`
      <li
        part="item"
        class=${classMap({
          'item':           true,
          'item--disabled': !!item.disabled,
          'item--active':   isSubmenuOpen,
          'item--focused':  isFocused,
          'item--selected': isSelected,
          'item--danger':   !!item.danger,
        })}
        style=${item.style ? styleMap(item.style as Record<string, string>) : ''}
        data-render-index=${renderIdx}
        role="none"
        @mouseenter=${() => this._handleItemMouseEnter(renderIdx, item)}
      >
        ${item.url && !item.disabled
          ? html`
            <a
              href=${item.url}
              target=${item.target ?? '_self'}
              title=${item.title ?? ''}
              role="menuitem"
              data-render-index=${renderIdx}
              class="item-inner"
              tabindex=${isFocused ? '0' : '-1'}
              @click=${(e: Event) => { this._selectedRIdx = renderIdx; this._executeItem(item, e); }}
            >
              ${this._renderItemInner(item, hasSubmenu)}
            </a>`
          : html`
            <button
              type="button"
              role="menuitem"
              data-render-index=${renderIdx}
              class="item-inner"
              tabindex=${this.inline ? '0' : (isFocused ? '0' : '-1')}
              ?disabled=${item.disabled}
              title=${item.title ?? ''}
              aria-haspopup=${hasSubmenu ? 'menu' : 'false'}
              aria-expanded=${hasSubmenu ? String(isSubmenuOpen) : 'false'}
              @click=${(e: Event) => this._handleItemClick(item, renderIdx, e)}
              @focus=${() => { this._focusedRIdx = renderIdx; }}
            >
              ${this._renderItemInner(item, hasSubmenu)}
            </button>`
        }

        ${hasSubmenu && isSubmenuOpen ? html`
          <ul
            class="submenu-panel"
            part="submenu-panel"
            role="menu"
            data-submenu-ridx=${renderIdx}
          >
            ${repeat(
              (item.items ?? []).filter(sub => sub.visible !== false),
              (sub) => sub.id ?? sub.label,
              (sub, subIdx) => this._renderSubItem(sub, subIdx),
            )}
          </ul>` : nothing}
      </li>`;
  }

  private _renderItemInner(item: MenuItem, hasSubmenu: boolean): TemplateResult {
    return html`
      ${item.icon
        ? html`<span part="item-icon" class="item-icon ${item.iconClass ?? ''}"><uwc-icon name=${item.icon} size="1.05em"></uwc-icon></span>`
        : nothing}
      <span part="item-label" class="item-label">${item.label ?? ''}</span>
      ${item.badge
        ? html`<span part="item-badge" class="item-badge ${item.badgeClass ?? ''}">${item.badge}</span>`
        : nothing}
      ${hasSubmenu
        ? html`<uwc-icon name="chevron-right" size="1em" class="item-chevron" aria-hidden="true"></uwc-icon>`
        : nothing}`;
  }

  private _renderSubItem(item: MenuItem, subIdx: number): TemplateResult {
    if (item.separator) {
      return html`<li class="separator" role="separator" aria-hidden="true"></li>`;
    }
    return html`
      <li role="none" class=${classMap({ 'item': true, 'item--disabled': !!item.disabled })}>
        <button
          type="button"
          role="menuitem"
          data-sub-item-index=${subIdx}
          class="item-inner"
          tabindex="-1"
          ?disabled=${item.disabled}
          title=${item.title ?? ''}
          @click=${(e: Event) => {
            e.stopPropagation();
            if (!item.disabled) this._executeItem(item, e);
          }}
        >
          ${this._renderItemInner(item, false)}
        </button>
      </li>`;
  }

  // ── Public API ───────────────────────────────────────────────────

  show():   void { this._pc.show(); }
  hide():   void { this._pc.hide(); }
  toggle(): void { this._pc.toggle(); }
  get isOpen(): boolean { return this._pc.open; }

  // ── Render ───────────────────────────────────────────────────────

  protected override render() {
    const isExternal = Boolean(this.triggerId);
    const open       = this._pc.open;

    return html`
      ${!isExternal && !this.inline
        ? html`<slot name="trigger" part="trigger"></slot>`
        : nothing}

      <div
        id="panel"
        part="panel"
        popover=${ifDefined(this.inline ? undefined : 'manual')}
        role="menu"
        class="panel ${(open || this.inline) ? 'is-open' : ''}"
        data-placement=${this.placement}
        aria-orientation="vertical"
        @keydown=${this.inline ? this._onMenuKeydown : nothing}
      >
        <slot name="start"></slot>
        <ul class="list" role="presentation">
          ${repeat(
            this._flatList,
            (entry) => entry.renderIdx,
            (entry) => this._renderEntry(entry),
          )}
        </ul>
        <slot name="end"></slot>
      </div>`;
  }

  static override styles = [styles];
}

declare global {
  interface HTMLElementTagNameMap { 'uwc-menu': UwcMenu; }
}
